import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FWLogo } from "@/components/BrandMarks";

const PILLARS = [
    { n: "01", name: "Physical Performance", desc: "Fitness, movement, warm-ups, cardio tennis, cross-training, recovery, and the physical tools that support your game." },
    { n: "02", name: "Mental Performance", desc: "Sport psychology, match routines, parent coaching, game reviews, confidence, focus, and preparation for competition." },
    { n: "03", name: "Technical Development", desc: "Private coaching, small-group training, stroke development, tactical skills, singles strategy, doubles strategy, and purposeful repetition." },
    { n: "04", name: "Gear Optimization", desc: "Stringing, racquet customization, grip, weight, balance, gear recommendations, and The Lab." },
];

export default function FrameworkHome() {
    return (
        <div data-testid="framework-home">
            {/* HERO */}
            <section className="relative overflow-hidden min-h-[92vh] flex items-end">
                <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        backgroundImage: "url('/brand/framework-doorway.png')",
                        backgroundSize: "cover",
                        backgroundPosition: "center 30%",
                    }}
                />
                <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.95) 100%)",
                    }}
                />

                <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 pt-32 w-full">
                    <div className="grid md:grid-cols-12 gap-8 items-end">
                        <div className="md:col-span-8">
                            <div className="flex items-center gap-4 mb-6">
                                <FWLogo size={44} />
                                <span className="fw-label-lime">Mind. Body. Game.</span>
                            </div>

                            <h1 className="fw-display text-white text-5xl sm:text-6xl lg:text-8xl">
                                Better Systems. <br />
                                <span style={{ color: "#a6ce39" }}>Better Players.</span>
                            </h1>

                            <p className="mt-8 text-white/80 max-w-xl text-lg leading-relaxed">
                                Framework Tennis connects physical performance, mental performance,
                                technical development, and gear optimization into one individualized
                                coaching experience.
                            </p>

                            <p className="mt-4 text-white/90 max-w-xl text-lg leading-relaxed">
                                Your body. Your mind. Your coach. Your gear. No gaps in your game.
                            </p>

                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link to="/framework/services" className="fw-btn-primary" data-testid="fw-home-cta-book">
                                    Explore the Framework <ArrowRight size={16} strokeWidth={2.5} />
                                </Link>
                                <Link to="/framework/swing-analysis" className="fw-btn-outline" data-testid="fw-home-cta-swing">
                                    Try Swing AI
                                </Link>
                            </div>
                        </div>

                        <div className="md:col-span-4">
                            <div className="border border-white/15 bg-black/70 backdrop-blur-md p-6">
                                <div className="fw-label-lime mb-4">Player System</div>
                                <ul className="space-y-3 fw-mono text-xs text-white/70">
                                    {["PHYSICAL", "MENTAL", "TECHNICAL", "GEAR"].map((k) => (
                                        <li key={k} className="flex justify-between border-b border-white/8 pb-2">
                                            <span>{k}</span>
                                            <span className="text-[#a6ce39]">READY</span>
                                        </li>
                                    ))}
                                    <li className="flex justify-between">
                                        <span>TECH.LAYER</span>
                                        <span className="text-[#a6ce39] animate-pulse">LIVE</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
                    <div>
                        <div className="fw-label-lime mb-4">01 // The Framework</div>
                        <h2 className="fw-display text-white text-4xl sm:text-6xl">
                            Four pillars. One player.
                        </h2>
                    </div>

                    <p className="text-white/70 max-w-md leading-relaxed">
                        Most coaching isolates problems. Framework connects them.
                        Lasting improvement happens when every part of your game works together.
                    </p>
                </div>

                <div className="divide-y divide-white/10 border-y border-white/10">
                    {PILLARS.map((p) => (
                        <div key={p.n} className="grid md:grid-cols-12 gap-6 py-6 hover:bg-white/[0.02] transition-colors">
                            <div className="md:col-span-1 fw-mono text-sm text-[#a6ce39]">{p.n}</div>
                            <div className="md:col-span-3">
                                <h3 className="fw-heading text-2xl text-white">{p.name}</h3>
                            </div>
                            <div className="md:col-span-6">
                                <p className="text-white/70 leading-relaxed">{p.desc}</p>
                            </div>
                            <div className="md:col-span-2 md:text-right">
                                <Link to="/framework/services" className="fw-mono text-xs uppercase tracking-[0.25em] text-white/80 link-underline">
                                    Learn More →
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* EVERYTHING CONNECTS */}
            <section className="relative overflow-hidden border-t border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-5">
                        <div className="fw-label-lime mb-4">02 // Everything Connects</div>

                        <h2 className="fw-display text-white text-4xl sm:text-5xl">
                            This is not a menu. <br />
                            It is a <span style={{ color: "#a6ce39" }}>system.</span>
                        </h2>

                        <p className="mt-6 text-white/70 leading-relaxed">
                            Lasting improvement does not come from fixing one thing. It comes from
                            understanding how technique, movement, psychology, recovery, equipment,
                            and environment work together.
                        </p>

                        <p className="mt-4 text-white/70 leading-relaxed">
                            Technique affects confidence. Equipment changes movement. Recovery changes
                            output. Psychology changes decision-making. When those connections improve,
                            performance follows.
                        </p>

                        <Link to="/framework/coaches" className="fw-btn-outline mt-8" data-testid="fw-home-meet-coaches">
                            Meet the Team <ArrowRight size={16} />
                        </Link>
                    </div>

                    <div className="md:col-span-7 relative">
                        <div className="aspect-[4/3] overflow-hidden border border-white/10 bg-black">
                            <img
                                src="/brand/framework-brain-ball.png"
                                alt="Framework Tennis connected coaching system"
                                className="w-full h-full object-contain"
                                style={{ background: "#fff" }}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* TECHNOLOGY */}
            <section className="border-t border-white/10 bg-[#0a0a0a]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <div className="fw-label-lime mb-4">03 // Technology Layer</div>
                        <h2 className="fw-display text-white text-4xl sm:text-5xl">
                            Coaching first. <br />
                            Technology where it helps.
                        </h2>
                        <p className="mt-6 text-white/70 leading-relaxed max-w-2xl">
                            Technology is not the core of Framework. It is one of the tools we use
                            to make coaching more specific, more accessible, and more individualized.
                            Swing AI, video analysis, and future player tools live here.
                        </p>
                    </div>

                    <div className="md:col-span-5">
                        <div className="border border-white/10 bg-black/60 p-6">
                            <div className="fw-label-lime mb-4">Current Tools</div>
                            <ul className="space-y-3 text-white/70 leading-relaxed">
                                <li>Swing AI analysis</li>
                                <li>Video review sessions</li>
                                <li>Virtual coaching support</li>
                                <li>SwingVision and match review options</li>
                                <li>Future ball machine and player tracking tools</li>
                            </ul>
                            <Link to="/framework/swing-analysis" className="fw-btn-outline mt-8">
                                Try Swing AI <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-white/10 bg-black">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                        <div className="fw-label-lime mb-2">Ready to Build?</div>
                        <h3 className="fw-display text-3xl md:text-5xl text-white">
                            Start anywhere. <span style={{ color: "#a6ce39" }}>Connect everything.</span>
                        </h3>
                    </div>

                    <div className="flex flex-wrap gap-4">
                        <Link to="/framework/services" className="fw-btn-primary" data-testid="fw-home-strip-book">
                            Book a Session
                        </Link>
                        <Link to="/framework/contact" className="fw-btn-outline" data-testid="fw-home-strip-contact">
                            Contact
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}